// Shared types for the orders import wizard (Settings → Import → Orders).
import type { CargoItemKindValue } from '@my/tms-api';

/**
 * One normalised job row parsed from the import file. In this layout a single
 * row is a complete order: a customer, a cargo line, and a collection +
 * delivery leg. Multi-stop / multi-cargo trees aren't expressed in v1.
 */
export interface JobRow {
  /** Source reference (e.g. `JOB-0001`). Shown in the UI and stamped onto the
   *  order as `reference1`. NOT the TMS order number — that's allocated at commit. */
  jobRef: string;
  accountCode: string;
  clientName: string;
  collectionAddress: string;
  collectionDate: string; // ISO yyyy-MM-dd (verbatim from the file)
  collectionTime: string; // HH:mm
  deliveryAddress: string;
  deliveryDate: string;
  deliveryTime: string;
  itemType: string;
  quantity: number | null;
  /** Parsed from e.g. `"-18C"` / `"2C"`; null for `"Ambient"` or blank. */
  temperatureC: number | null;
  notes: string;
}

export type MatchKind = 'code' | 'name' | 'none';
export type DraftStatus = 'pending' | 'creating' | 'created' | 'failed' | 'skipped';

/**
 * A job after customer + cargo resolution, carrying selection and commit state.
 * `key` is a stable per-import id (NOT the order number) used as the React key
 * and the BE `rowRef` correlation handle.
 */
export interface OrderDraft {
  key: string;
  job: JobRow;

  // Resolution
  customerPartyId: string | null;
  customerLabel: string;
  customerMatch: MatchKind;
  cargoDefinitionId: string | null;
  cargoLabel: string;
  cargoKind: CargoItemKindValue;
  cargoMatch: boolean;
  /** Default cargo item picked for the matched definition (e.g. a pallet
   *  variant name) — becomes the cargo line's `description`. Null when the
   *  definition has no variants. */
  cargoVariantName: string | null;
  /** Loading metres per unit from the default variant (pallets) — the cargo
   *  line's `loadingMetresM` is this × quantity. Null when not applicable. */
  cargoLoadingMetresPerUnit: number | null;

  // Selection
  excluded: boolean;

  // Commit state
  /** Allocated from the org sequence at commit; kept so a failed order reuses
   *  its number on retry instead of burning a fresh one. */
  orderNumber?: string;
  status_: DraftStatus;
  error?: string;
}

/** A draft has everything it needs to be sent once customer + cargo resolve. */
export function isResolvable(d: OrderDraft): boolean {
  return d.customerPartyId !== null && d.cargoDefinitionId !== null;
}
